import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Switch from '@mui/material/Switch';

interface LeaveType {
  id: number; name: string; color: string; allowance: number | null;
  allowanceUnit: string; displayUnit: string; allowsPartialDay: boolean;
  requiresApproval: boolean; isActive: boolean; isDefault: boolean;
}

const HrLeaveTypeSettings: React.FC = () => {
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<LeaveType>>({ name: '', color: '#43c98a', allowanceUnit: 'days', displayUnit: 'days', allowsPartialDay: true, requiresApproval: true, isDefault: false });

  const load = useCallback(async () => {
    try { const { data } = await axios.get('hr-leave-type'); setTypes(Array.isArray(data) ? data : []); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    try {
      if (editId) await axios.put(`hr-leave-type/${editId}`, form);
      else await axios.post('hr-leave-type/add', form);
      setAdding(false); setEditId(null); load();
    } catch {}
  };

  const toggleActive = async (lt: LeaveType) => {
    try { await axios.put(`hr-leave-type/${lt.id}`, { isActive: !lt.isActive }); load(); } catch {}
  };

  const renderForm = () => (
    <div className="bg-[var(--btn-user-form)] border border-[var(--border-input)] rounded-[5px] p-[12px] mb-[8px]">
      <div className="flex gap-[8px] mb-[8px]">
        <input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder={t('WorkingStatusName')} className="flex-1 text-[13px] bg-[var(--bg-input)] text-[var(--text-color)] rounded-[5px] px-[8px] py-[4px] timesheet-edit-input" />
        <input type="color" value={form.color || '#43c98a'} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
          title={t('WorkingStatusColor')} style={{ width: 36, height: 34, borderRadius: 6, border: '1px solid var(--border-input)', padding: 2, cursor: 'pointer' }} />
      </div>
      <div className="grid grid-cols-2 gap-[8px] mb-[8px]">
        <div>
          <label className="text-[11px] block mb-[2px]" style={{ color: 'var(--text-color-calendar-week)' }}>Allowance</label>
          <input type="number" value={form.allowance ?? ''} onChange={e => setForm(f => ({ ...f, allowance: e.target.value ? Number(e.target.value) : null }))}
            placeholder="null = unlimited" className="w-full text-[13px] bg-[var(--bg-input)] text-[var(--text-color)] rounded-[5px] px-[8px] py-[4px] timesheet-edit-input" />
        </div>
        <div>
          <label className="text-[11px] block mb-[2px]" style={{ color: 'var(--text-color-calendar-week)' }}>Unit / Display</label>
          <div className="flex gap-[4px]">
            <select value={form.allowanceUnit} onChange={e => setForm(f => ({ ...f, allowanceUnit: e.target.value }))}
              className="flex-1 text-[12px] bg-[var(--bg-input)] text-[var(--text-color)] rounded-[5px] px-[4px] py-[4px] timesheet-edit-input">
              <option value="days">days</option><option value="hours">hours</option>
            </select>
            <select value={form.displayUnit} onChange={e => setForm(f => ({ ...f, displayUnit: e.target.value }))}
              className="flex-1 text-[12px] bg-[var(--bg-input)] text-[var(--text-color)] rounded-[5px] px-[4px] py-[4px] timesheet-edit-input">
              <option value="days">days</option><option value="hours">hours</option><option value="shifts">shifts</option>
            </select>
          </div>
        </div>
      </div>
      <div className="flex gap-[12px] mb-[8px] text-[12px]" style={{ color: 'var(--text-color)' }}>
        <label className="flex items-center gap-[4px]"><input type="checkbox" checked={!!form.requiresApproval} onChange={e => setForm(f => ({ ...f, requiresApproval: e.target.checked }))} /> Requires approval</label>
        <label className="flex items-center gap-[4px]"><input type="checkbox" checked={!!form.allowsPartialDay} onChange={e => setForm(f => ({ ...f, allowsPartialDay: e.target.checked }))} /> Partial day</label>
        <label className="flex items-center gap-[4px]"><input type="checkbox" checked={!!form.isDefault} onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} /> Default</label>
      </div>
      <div className="flex justify-end gap-[6px]">
        <button type="button" title="Save" onClick={handleSave} style={{ color: '#43c98a' }}><CheckIcon sx={{ fontSize: 18 }} /></button>
        <button type="button" title="Cancel" onClick={() => { setAdding(false); setEditId(null); }} style={{ color: '#f76f8e' }}><CloseIcon sx={{ fontSize: 18 }} /></button>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex justify-end mb-[10px]">
        {!adding && !editId && (
          <button type="button" onClick={() => setAdding(true)}
            className="btn-primary">
            <AddIcon sx={{ fontSize: 14 }} /> {t('Add')}
          </button>
        )}
      </div>
      {adding && !editId && renderForm()}
      <div className="flex flex-col gap-[6px]">
        {types.map(lt => (
          <div key={lt.id}>
            {editId === lt.id ? renderForm() : (
              <div className="flex items-center gap-[8px] p-[8px] rounded-[5px] bg-[var(--btn-user-form)] border border-[var(--border-input)]" style={{ opacity: lt.isActive ? 1 : 0.5 }}>
                <span style={{ width: 12, height: 12, borderRadius: '50%', background: lt.color, flexShrink: 0, display: 'inline-block' }} />
                <div className="flex-1">
                  <span className="text-[13px] font-semibold" style={{ color: 'var(--text-color)' }}>{lt.name}</span>
                  {lt.isDefault && <span className="ml-[6px] text-[10px] px-[5px] py-[1px] rounded-[3px] bg-blue-500 text-white">default</span>}
                  <p className="text-[11px] mt-[1px]" style={{ color: 'var(--text-color-calendar-week)' }}>
                    {lt.allowance ?? '∞'} {lt.allowanceUnit} · show in {lt.displayUnit}
                  </p>
                </div>
                <Switch size="small" checked={lt.isActive} onChange={() => toggleActive(lt)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#43c98a' } }} />
                <button type="button" onClick={() => { setEditId(lt.id); setForm({ ...lt }); }}
                  className="text-[11px] px-[8px] py-[2px] rounded-[5px] bg-[var(--btn-user-in)] text-[var(--text-color)] hover:bg-[var(--btn-user-hover)]">Edit</button>
              </div>
            )}
          </div>
        ))}
        {types.length === 0 && !adding && (
          <div className="text-center py-[20px] text-[13px]" style={{ color: 'var(--text-color-calendar-week)', opacity: 0.6 }}>{t('Empty')}</div>
        )}
      </div>
    </div>
  );
};

export default HrLeaveTypeSettings;
